import { create } from 'zustand';
import { persist } from 'zustand/middleware';

type ThemeMode = 'light' | 'dark' | 'system';
type SidebarView = 'expanded' | 'collapsed' | 'hidden';
type ReadingPanePosition = 'right' | 'bottom' | 'off';
type DensityMode = 'comfortable' | 'cozy' | 'compact';
type AccentColor = 'blue' | 'green' | 'yellow' | 'red' | 'pink' | 'gray' | 'teal' | 'purple' | 'orange';

export interface ThemePalette {
  id: string;
  name: string;
  mode: 'light' | 'dark';
  sidebar: string;       // Sidebar background color
  content: string;       // Main content area background
  composeColor: string;  // Compose button background
  sidebarFg: 'light' | 'dark'; // 'light' = sidebar is dark bg (needs white text), 'dark' = sidebar is light bg (needs dark text)
  description: string;
  // Legacy aliases for backwards compat with preview thumbnails
  light: string;
  dark: string;
  preview: string;
}

export const themePalettes: ThemePalette[] = [
  {
    id: 'clean-light',
    name: 'Clean Light',
    mode: 'light',
    sidebar: '#ffffff',
    content: '#f8f9fa',
    composeColor: '#1967d2',
    sidebarFg: 'dark',
    light: '#f8f9fa',
    dark: '#ffffff',
    preview: 'clean-light',
    description: 'Crisp white Gmail-style light theme',
  },
  {
    id: 'crimson',
    name: 'Crimson',
    mode: 'light',
    sidebar: '#0f172a',
    content: '#ffffff',
    composeColor: '#dc2626',
    sidebarFg: 'light',
    light: '#ffffff',
    dark: '#0f172a',
    preview: 'crimson',
    description: 'Dark navy sidebar with crimson red accents',
  },
];

// Accent color palette mapping
export const accentColorPalette: Record<AccentColor, { main: string; light: string; dark: string }> = {
  blue: { main: '#1a73e8', light: '#4285f4', dark: '#1557b0' },
  green: { main: '#34a853', light: '#5bb974', dark: '#1e8e3e' },
  yellow: { main: '#f9ab00', light: '#fdd835', dark: '#f57c00' },
  red: { main: '#ea4335', light: '#f28b82', dark: '#c5221f' },
  pink: { main: '#e91e63', light: '#f48fb1', dark: '#c2185b' },
  gray: { main: '#455a64', light: '#78909c', dark: '#37474f' },
  teal: { main: '#00bcd4', light: '#4dd0e1', dark: '#0097a7' },
  purple: { main: '#9c27b0', light: '#ba68c8', dark: '#7b1fa2' },
  orange: { main: '#ff5722', light: '#ff8a65', dark: '#e64a19' },
};

interface ThemeState {
  mode: ThemeMode;
  actualMode: 'light' | 'dark';
  accentColor: AccentColor;
  selectedPalette: string;
  sidebarView: SidebarView;
  readingPanePosition: ReadingPanePosition;
  densityMode: DensityMode;
  showConversationView: boolean;
  previewOnHover: boolean;
  keyboardShortcutsEnabled: boolean;
  
  // Actions
  setMode: (mode: ThemeMode) => void;
  setAccentColor: (color: AccentColor) => void;
  setSelectedPalette: (paletteId: string) => void;
  setSidebarView: (view: SidebarView) => void;
  setReadingPanePosition: (position: ReadingPanePosition) => void;
  setDensityMode: (density: DensityMode) => void;
  toggleConversationView: () => void;
  togglePreviewOnHover: () => void;
  toggleKeyboardShortcuts: () => void;
}

const getSystemTheme = (): 'light' | 'dark' => {
  if (typeof window !== 'undefined') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'light';
};

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      mode: 'light',
      actualMode: 'light',
      accentColor: 'blue',
      selectedPalette: 'clean-light',
      sidebarView: 'expanded',
      readingPanePosition: 'right',
      densityMode: 'comfortable',
      showConversationView: true,
      previewOnHover: true,
      keyboardShortcutsEnabled: true,

      setMode: (mode) => {
        console.log('🎨 themeStore.setMode called with:', mode);
        const actualMode = mode === 'system' ? getSystemTheme() : mode;
        console.log('🎨 themeStore setting actualMode to:', actualMode);
        set({ mode, actualMode });
      },

      setAccentColor: (accentColor) => {
        console.log('🎨 themeStore.setAccentColor called with:', accentColor);
        set({ accentColor });
      },

      setSelectedPalette: (selectedPalette) => {
        console.log('🎨 themeStore.setSelectedPalette called with:', selectedPalette);
        const palette = themePalettes.find(p => p.id === selectedPalette);
        const actualMode = palette ? palette.mode : get().actualMode;
        console.log('🎨 themeStore setting palette → actualMode:', actualMode);
        set({ selectedPalette, mode: actualMode, actualMode });
      },

      setSidebarView: (sidebarView) => set({ sidebarView }),
      
      setReadingPanePosition: (readingPanePosition) => set({ readingPanePosition }),
      
      setDensityMode: (densityMode) => set({ densityMode }),
      
      toggleConversationView: () => set({ showConversationView: !get().showConversationView }),
      
      togglePreviewOnHover: () => set({ previewOnHover: !get().previewOnHover }),
      
      toggleKeyboardShortcuts: () => set({ keyboardShortcutsEnabled: !get().keyboardShortcutsEnabled }),
    }),
    {
      name: 'theme-storage',
      version: 3,
      migrate: (persistedState: any) => {
        // v3: reading pane was being forced to 'bottom' by an auto-adjust bug
        // on tablet-sized screens. Reset to the default 'right' so the user's
        // explicit choice is respected going forward.
        // NOTE: zustand passes the STATE OBJECT directly (not {state, version}).
        if (persistedState && typeof persistedState === 'object') {
          persistedState.readingPanePosition = 'right';
        }
        return persistedState;
      },
      partialize: (state) => {
        // Include readingPanePosition in persistence for hybrid layout
        return state;
      },
    }
  )
);

// Listen for system theme changes
if (typeof window !== 'undefined') {
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
    const state = useThemeStore.getState();
    if (state.mode === 'system') {
      useThemeStore.setState({ actualMode: e.matches ? 'dark' : 'light' });
    }
  });
}
